import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanAdminSeminarSeriesDetail,
  getYowanAdminPublicVisibilityStatusUi,
  getYowanPublicVisibilityDiagnostics,
  getYowanPublicVisibilityMessages,
  getYowanSeminarSlotWarningIssues,
  getYowanSeriesAdminWarningIssues,
  getYowanImageDisplayName,
  getYowanImagePreviewSrc,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const ReadOnlyField = ({ label, value }) => (
  <label className="c-form__field yowan-admin-readonly-field">
    <span className="c-form__label">{label}</span>
    <input className="c-form__control" readOnly type="text" value={value || '-'} />
  </label>
);

const ReadOnlyTextArea = ({ label, value }) => (
  <label className="c-form__field yowan-admin-readonly-field">
    <span className="c-form__label">{label}</span>
    <textarea className="c-form__control c-form__control--textarea" readOnly rows={3} value={value || '-'} />
  </label>
);

const ReadOnlyImageField = ({ label, image = null }) => {
  const imageSrc = getYowanImagePreviewSrc(image);
  const imageLabel = getYowanImageDisplayName(image);

  return (
    <div className="c-form__field yowan-admin-readonly-field">
      <span className="c-form__label">{label}</span>
      {imageSrc ? (
        <div className="yowan-admin-current-image">
          <img
            className="yowan-admin-current-image__thumbnail"
            src={imageSrc}
            alt={image?.alt_text || image?.altText || imageLabel || label}
          />
          <div className="yowan-admin-current-image__body">
            <p className="yowan-admin-current-image__label">{imageLabel || label}</p>
            <p className="yowan-admin-current-image__meta">公開シリーズ詳細のヘッダー画像として優先表示されます。</p>
          </div>
        </div>
      ) : (
        <input className="c-form__control" readOnly type="text" value="-" />
      )}
    </div>
  );
};

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const PublicVisibilityPanel = ({ visibility, fallbackMessage }) => {
  const statusUi = getYowanAdminPublicVisibilityStatusUi(visibility, {
    unknownLabel: '配下セミナー参照',
  });
  const messages = getYowanPublicVisibilityMessages(visibility);
  const diagnostics = getYowanPublicVisibilityDiagnostics(visibility);

  return (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">公開面表示判定</p>
        <StatusBadge label={statusUi.label} tone={statusUi.tone} />
      </div>
      {diagnostics.length ? (
        <dl className="yowan-admin-event-detail__definition">
          {diagnostics.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {messages.length ? (
        <ul className="yowan-admin-public-visibility__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : (
        <p className="yowan-admin-public-visibility__message">
          {fallbackMessage || '公開面に表示できる状態です。'}
        </p>
      )}
    </section>
  );
};

const AdminStateNoticePanel = ({ title, issues = [] }) => (
  issues.length ? (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">{title}</p>
      </div>
      <ul className="yowan-admin-public-visibility__list">
        {issues.map((issue) => (
          <li key={issue.code || issue.label}>{issue.message}</li>
        ))}
      </ul>
    </section>
  ) : null
);

const normalizeSeriesKindLabel = (label = '') => (
  label === '一連の講習会' ? 'シリーズ' : label
);

const seminarColumnStyle = {
  width: '24rem',
  minWidth: '24rem',
};

const seminarApplicationsColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
  whiteSpace: 'nowrap',
};

const seminarActionColumnStyle = {
  width: '16rem',
  minWidth: '16rem',
  whiteSpace: 'nowrap',
};

const getSeriesChildSeminarWarnings = (seminar = {}) => {
  const issues = [];

  if (seminar.isPublished === false) {
    issues.push({
      code: 'seminar_unpublished',
      label: '非公開',
    });
  }

  if (seminar.isActive === false) {
    issues.push({
      code: 'seminar_inactive',
      label: '停止中',
    });
  }

  return [
    ...issues,
    ...getYowanSeminarSlotWarningIssues(seminar),
  ];
};

const buildSeriesDuplicateHref = (seriesId, preservedDomainSearch) => appendYowanPreviewQueryIfNeeded(
  `/admin/events/new?duplicateSeriesFrom=${seriesId}`,
  preservedDomainSearch,
);

const AdminEventSeriesDetailPage = () => {
  const content = yowanPageContent['/admin/events/series/:seriesId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { seriesId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminSeminarSeriesDetail(seriesId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('シリーズを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [seriesId]);

  if (!content) {
    return null;
  }

  const seminars = record?.seminars || [];
  const headerTitle = record ? `${record.title} 詳細` : content.header.title;
  const seriesWarningIssues = getYowanSeriesAdminWarningIssues(record || {});

  return (
    <AdminBlogShell activeNavHref="/admin/events/seminars" routeKey={content.routeKey}>
      <AdminPageHeader
        title={headerTitle}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="seminars" to={appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch)}>
            セミナー
          </Link>,
          <Link key="series" to={appendYowanPreviewQueryIfNeeded('/admin/events/seminars?tab=series', preservedDomainSearch)}>
            シリーズ
          </Link>,
          record ? (
            <Link key="edit" to={appendYowanPreviewQueryIfNeeded(`/admin/events/series/${record.id}/edit`, preservedDomainSearch)}>
              シリーズ編集
            </Link>
          ) : null,
          record ? (
            <Link key="messages" to={appendYowanPreviewQueryIfNeeded(`/admin/events/series/${record.id}/application-messages`, preservedDomainSearch)}>
              申込メッセージ
            </Link>
          ) : null,
          record?.publicHref ? (
            <Link key="public" to={appendYowanPreviewQueryIfNeeded(record.publicHref, preservedDomainSearch)}>
              公開ページ
            </Link>
          ) : null,
          record ? (
            <Link key="duplicate" to={buildSeriesDuplicateHref(record.id, preservedDomainSearch)}>
              シリーズ複製
            </Link>
          ) : null,
          record ? (
            <Link key="add-seminar" to={appendYowanPreviewQueryIfNeeded(record.addSeminarHref, preservedDomainSearch)}>
              セミナー追加
            </Link>
          ) : null,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="シリーズを読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
              <header className="yowan-admin-event-detail__section-header">
                <h2 className="yowan-admin-detail-panel__title">シリーズ情報</h2>
                <p className="yowan-admin-detail-panel__lead">
                  公開面でシリーズとしてまとめる単位です。公開状態は配下セミナーから導出されます。
                </p>
              </header>
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={normalizeSeriesKindLabel(record.seriesKindLabel) || '-'} tone="review" />
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '有効' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
              </div>
              <PublicVisibilityPanel
                visibility={record.publicVisibility}
                fallbackMessage="シリーズが有効な場合、公開条件を満たす配下セミナーが公開面に表示されます。"
              />
              <AdminStateNoticePanel title="シリーズ内の注意" issues={seriesWarningIssues} />
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="シリーズ名" value={record.title} />
                <ReadOnlyField label="補足タイトル" value={record.subtitle} />
                <ReadOnlyField label="公開URL slug" value={record.publicSlug} />
                <ReadOnlyField label="公開パス" value={record.publicPath || record.publicHref} />
                <ReadOnlyField label="正規URL" value={record.canonicalUrl} />
                <ReadOnlyTextArea label="シリーズ概要" value={record.summary} />
                <ReadOnlyImageField label="シリーズヘッダー画像" image={record.primary_image} />
                <ReadOnlyField label="主催者" value={record.organizerName} />
                <ReadOnlyField label="通し参加料金" value={record.fullParticipationFeeLabel} />
                <ReadOnlyField label="セミナー" value={`${record.seminarsCount || 0}件`} />
                <ReadOnlyField label="申込者" value={`${record.applicationsCount || 0}名`} />
                <ReadOnlyField label="初回開催" value={record.firstSlotAt} />
                <ReadOnlyField label="最終開催" value={record.lastSlotAt} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
              </div>
              {record.publicHref ? (
                <div className="yowan-admin-event-detail__related">
                  <p className="yowan-admin-detail-panel__meta">公開ページ</p>
                  <div className="yowan-admin-event-detail__related-links">
                    <Link
                      className="yowan-admin-event-detail__related-link"
                      to={appendYowanPreviewQueryIfNeeded(record.publicHref, preservedDomainSearch)}
                    >
                      {record.publicPath || record.publicHref}
                    </Link>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
              <header className="yowan-admin-event-detail__section-header">
                <h2 className="yowan-admin-detail-panel__title">配下セミナー</h2>
                <p className="yowan-admin-detail-panel__lead">
                  シリーズに含まれるセミナーを確認します。セミナー本文や開催コマは各セミナーの編集画面で管理します。
                </p>
              </header>
              {seminars.length ? (
                <div className="yowan-admin-resource-table-wrap">
                  <table className="yowan-admin-resource-table">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col" style={seminarColumnStyle}>セミナー</th>
                        <th scope="col">コマ</th>
                        <th scope="col" style={seminarApplicationsColumnStyle}>申込者</th>
                        <th scope="col">公開</th>
                        <th scope="col">公開面</th>
                        <th scope="col">有効</th>
                        <th scope="col" style={seminarActionColumnStyle}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seminars.map((seminar, index) => {
                        const childWarnings = getSeriesChildSeminarWarnings(seminar);
                        const childPublicVisibilityUi = getYowanAdminPublicVisibilityStatusUi(seminar.publicVisibility);

                        return (
                          <tr key={seminar.id || index}>
                            <td>{index + 1}</td>
                            <td style={seminarColumnStyle}>
                              <p className="yowan-admin-resource-table__title">{seminar.title || '-'}</p>
                              <p className="yowan-admin-resource-table__subline">{seminar.subtitle || seminar.summary || '-'}</p>
                              {childWarnings.length ? (
                                <p className="yowan-admin-resource-table__subline">
                                  {childWarnings.map((issue) => issue.label).join(' / ')}
                                </p>
                              ) : null}
                            </td>
                            <td>{seminar.slotsCount || 0}</td>
                            <td style={seminarApplicationsColumnStyle}>{seminar.applicationsCount || 0}名</td>
                            <td>
                              <StatusBadge label={seminar.isPublished ? '公開中' : '非公開'} tone={seminar.isPublished ? 'published' : 'draft'} />
                            </td>
                            <td title={getYowanPublicVisibilityMessages(seminar.publicVisibility).join(' / ')}>
                              <StatusBadge
                                label={childPublicVisibilityUi.label}
                                tone={childPublicVisibilityUi.tone}
                              />
                            </td>
                            <td>
                              <StatusBadge label={seminar.isActive ? '有効' : '停止中'} tone={seminar.isActive ? 'published' : 'draft'} />
                            </td>
                            <td style={seminarActionColumnStyle}>
                              <div className="yowan-admin-resource-table__actions">
                                <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/${seminar.id}`, preservedDomainSearch)}>詳細</Link>
                                <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/${seminar.id}/edit?kind=seminar`, preservedDomainSearch)}>編集</Link>
                                <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/${seminar.id}/applications`, preservedDomainSearch)}>参加者</Link>
                                <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/${seminar.id}/application-messages`, preservedDomainSearch)}>メッセージ</Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="yowan-admin-form__message">配下セミナーはまだありません。</p>
              )}
              {record.addSeminarHref ? (
                <div className="yowan-admin-event-detail__related">
                  <p className="yowan-admin-detail-panel__meta">セミナー追加</p>
                  <div className="yowan-admin-event-detail__related-links">
                    <Link
                      className="yowan-admin-event-detail__related-link"
                      to={appendYowanPreviewQueryIfNeeded(record.addSeminarHref, preservedDomainSearch)}
                    >
                      このシリーズにセミナーを追加
                    </Link>
                  </div>
                </div>
              ) : null}
            </section>

            {record?.isActive === false ? (
              <AdminStateNoticePanel
                title="シリーズ本体の注意"
                issues={[{
                  code: 'series_inactive',
                  label: 'シリーズ停止中',
                  message: 'シリーズ本体が停止中です。公開面へ出す場合はシリーズ編集画面で「有効にする」を確認してください。',
                }]}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminEventSeriesDetailPage;
